import React, { ReactElement, useMemo } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { Icon } from "semantic-ui-react";

import useTypedSelector from "hooks/useTypedSelector";
import { useRouter } from "hooks/useRouter";
import { selectAuthInitialized, selectSession } from "redux/selectors/authSelectors";
import { profileFromSession } from "auth/session";
import { openLoginModal } from "redux/auth/authSlice";
import { device } from "utils/devices";

/**
 * Top-bar profile button. Shows whether the user is logged in; when logged
 * out it opens the login modal, when logged in it goes to the profile page.
 */
export default function ProfileButton(): ReactElement {
  const router = useRouter();
  const session = useTypedSelector(selectSession);
  const initialized = useTypedSelector(selectAuthInitialized);
  const dispatch = useDispatch();

  const profile = useMemo(
    () => (session ? profileFromSession(session) : null),
    [session]
  );
  const isLoggedIn = profile !== null;
  const label = isLoggedIn ? profile.name || profile.email : "Log in";

  const handleClick = () => {
    if (isLoggedIn) router.push("/profile");
    else dispatch(openLoginModal());
  };

  return (
    <Button
      type="button"
      data-testid="profile-button"
      data-logged-in={isLoggedIn}
      className={isLoggedIn ? "logged-in" : "logged-out"}
      onClick={handleClick}
      disabled={!initialized}
      aria-label={isLoggedIn ? `Profile for ${label}` : "Log in"}
      title={isLoggedIn ? `Logged in as ${profile.email}` : "Not logged in"}
    >
      <span className="avatar">
        <Icon name="user" fitted />
        <span className="status-dot" aria-hidden="true" />
      </span>
      <span className="label">{label}</span>
    </Button>
  );
}

const Button = styled.button`
  && {
    margin-left: auto;
    margin-right: 0;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    max-width: 260px;
    padding: 8px 16px 8px 8px;
    border: 1px solid ${(props) => props.theme.tertiary};
    border-radius: 999px;
    background: #fff;
    color: ${(props) => props.theme.primary};
    font-weight: 600;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  &&:hover:not(:disabled),
  &&:focus-visible {
    border-color: ${(props) => props.theme.secondary};
    box-shadow: 0 0 0 3px rgba(99, 160, 219, 0.2);
    outline: none;
  }

  &&:disabled {
    opacity: 0.6;
    cursor: default;
  }

  && * {
    margin: 0;
  }

  .avatar {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    color: #fff;
    background-color: ${(props) => props.theme.tertiary};
  }

  &&.logged-in .avatar {
    background-color: ${(props) => props.theme.secondary};
  }

  .status-dot {
    position: absolute;
    right: -1px;
    bottom: -1px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 2px solid #fff;
    background-color: #b5b5b5;
  }

  &&.logged-in .status-dot {
    background-color: #21ba45;
  }

  .label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media ${device.tablet} {
    && {
      padding: 4px;
    }

    .label {
      display: none;
    }
  }
`;
